import React from 'react';

import type { DeviceGlyph } from './device';

/**
 * Page-local device silhouettes for the sessions list. Drawn inline (same
 * stroke style as the shared `Icon` set) so a session row can show a
 * form-factor-specific glyph without adding device icons to the shared,
 * claim-gated `Icon` component. Tint follows `currentColor` by default.
 */
export type DeviceIconProps = {
  glyph: DeviceGlyph;
  size?: number;
  color?: string;
  className?: string;
};

const PATHS: Record<DeviceGlyph, React.ReactNode> = {
  // Laptop — screen + base bar (Mac / Chromebook).
  laptop: (
    <>
      <rect x="5" y="5" width="14" height="10" rx="1.5" />
      <path d="M3 18.5h18" />
    </>
  ),
  // Desktop monitor on a stand (Windows / Linux PC).
  desktop: (
    <>
      <rect x="3.5" y="4.5" width="17" height="11.5" rx="1.5" />
      <path d="M9 20h6M12 16v4" />
    </>
  ),
  // Phone — tall rounded body with a home indicator (iPhone / Android phone).
  phone: (
    <>
      <rect x="7" y="2.5" width="10" height="19" rx="2.5" />
      <path d="M10.5 18.5h3" />
    </>
  ),
  // Tablet — wider rounded body (iPad / Android tablet).
  tablet: (
    <>
      <rect x="4.5" y="3" width="15" height="18" rx="2" />
      <path d="M10.5 18h3" />
    </>
  ),
  // Generic device — neutral rounded panel for an unrecognised user agent.
  generic: (
    <>
      <rect x="4" y="5" width="16" height="14" rx="2.5" />
      <path d="M8 19h8" />
    </>
  ),
};

export const DeviceIcon = ({
  glyph,
  size = 22,
  color = 'currentColor',
  className,
}: DeviceIconProps): React.ReactElement => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {PATHS[glyph]}
  </svg>
);

export default DeviceIcon;
